import { MapPin, ShieldCheck, Users } from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";

export const navbarCopyPl: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Specjalizacja",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Wyceń projekt",
};

export const aboutUsCopyPl = (
  overrides?: Partial<AboutUsCopy["header"]>,
): AboutUsCopy => ({
  header: {
    h2: "O nas",
    h3: "Polski software house — aplikacje mobilne, webowe i backend",
    p1: "Jesteśmy software house'em z Polski. Budujemy aplikacje mobilne i webowe, SaaS, panele administracyjne oraz integracje AI — od pierwszej rozmowy i NDA po MVP, wdrożenie i utrzymanie.",
    p2: "Kuba odpowiada za backend i infrastrukturę, Mateusz za UX produktów cyfrowych, a zespół mobilny za Flutter i aplikacje towarzyszące. Pracujesz bezpośrednio z ludźmi, którzy piszą kod.",
    p3: "Zgodność z RODO",
    p4: "Dane w UE",
    Icon: ShieldCheck,
    imageAlt: "Zespół Blues Brackets - aplikacje mobilne i webowe",
    ...overrides,
  },
  values: [
    {
      Icon: Users,
      title: "Pracujesz bezpośrednio z inżynierami",
      description:
        "Bez pośredników — osoba na rozmowie to osoba, która projektuje UX, buduje backend albo pisze aplikację Flutter.",
    },
    {
      Icon: MapPin,
      title: "Rozumiemy wymagania rynku UE",
      description:
        "Projektujemy z myślą o RODO, płatnościach europejskich i realnym wdrożeniu produktu dla klientów w Polsce i UE.",
    },
    {
      Icon: ShieldCheck,
      title: "Dbamy o bezpieczeństwo danych",
      description:
        "Dane użytkowników trzymamy w UE — pod kontrolą polskiego zespołu, bez zbędnych pośredników.",
    },
  ],
  cta: {
    text: "Napisz do nas",
    href: "#kontakt",
  },
});

export const contactFormPl: FooterCopy["contactForm"] = {
  heading: "Napisz do nas",
  fields: {
    name: {
      label: "Imię i nazwisko",
      placeholder: "Jan Kowalski",
      requiredError: "Podaj imię i nazwisko",
    },
    email: {
      label: "Adres e-mail",
      placeholder: "jan@firma.pl",
      requiredError: "Podaj adres e-mail",
      invalidError: "Podaj poprawny adres e-mail",
    },
    topic: {
      label: "Opis projektu",
      placeholder:
        "Krótko opisz aplikację, SaaS, panel admina lub integrację AI...",
      requiredError: "Opisz swój projekt",
      maxLengthError: "Opis może mieć maksymalnie 2000 znaków",
    },
  },
  submit: "Wyślij zapytanie",
  submitting: "Wysyłanie...",
  successMessage: "Wiadomość wysłana!",
  errorMessage: "Nie udało się wysłać wiadomości :(",
  responseTime: "Odpowiemy w ciągu 24 godzin w dni robocze.",
  emailSubjectPrefix: "Imię i nazwisko",
};

export const mobileClusterLinks = [
  {
    href: "/aplikacje-mobilne-i-webowe",
    text: "Aplikacje mobilne i webowe",
  },
  {
    href: "/aplikacje-flutter-android-ios",
    text: "Aplikacje Flutter",
  },
  {
    href: "/backend-i-panele-administracyjne",
    text: "Backend i panele administracyjne",
  },
  { href: "/saas-na-zamowienie", text: "SaaS na zamówienie" },
  {
    href: "/integracja-ai-w-aplikacji",
    text: "Integracja AI w aplikacji",
  },
  { href: "/bluetooth-low-energy", text: "Bluetooth Low Energy" },
  { href: "/iot", text: "Systemy IoT" },
  { href: "/blog", text: "Blog" },
  { href: "/projects", text: "Projekty" },
] as const;

export const mobileKnowledgeBaseLinks = [
  {
    href: "/pl/blogs/ile-kosztuje-aplikacja-mobilna-lub-webowa",
    text: "Ile kosztuje aplikacja mobilna lub webowa?",
  },
  {
    href: "/pl/blogs/jak-powstaje-aplikacja-od-ux-po-wdrozenie",
    text: "Jak powstaje aplikacja od UX po wdrożenie?",
  },
  {
    href: "/pl/blogs/flutter-vs-react-native-vs-natywnie",
    text: "Flutter, React Native czy natywnie?",
  },
  {
    href: "/pl/blogs/ile-kosztuje-wdrozenie-ai-w-aplikacji",
    text: "Ile kosztuje wdrożenie AI w aplikacji?",
  },
] as const;

export const organizationStructuredData = (description: string) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Blues Brackets",
  url: "https://bluesbrackets.com",
  logo: "https://bluesbrackets.com/favicon-dark.png",
  description,
  areaServed: "EU",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "hello@bluesbrackets.com",
    availableLanguage: ["Polish", "English"],
  },
});

export const serviceStructuredData = (
  name: string,
  description: string,
  serviceType: string,
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  provider: {
    "@type": "Organization",
    name: "Blues Brackets",
  },
  description,
  areaServed: "EU",
  serviceType,
});

export const faqStructuredData = (
  faqs: readonly { q: string; a: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(faq => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.a,
    },
  })),
});
